/**
 * Content Buckets Provider
 * 
 * Manages global state for content buckets including CRUD operations,
 * industry templates, and post assignments.
 * 
 * Features:
 * - Bucket management (create, read, update, delete)
 * - Industry template support
 * - Content ideas integration
 * - Post-bucket assignments
 * - Loading states and error handling
 * 
 * @author AI Assistant
 * @created 2024
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  getContentBuckets,
  createContentBucket,
  updateContentBucket,
  deleteContentBucket,
  getIndustryTemplates,
  createBucketsFromTemplate,
  getBucketContentIdeas,
  getContentIdeas,
  assignPostToBucket,
  removePostFromBucket
} from '../api';
import { useDemoMode } from './DemoModeProvider';
import { logError } from '../utils/logger';

const ContentBucketsContext = createContext(null);

// Demo data for content buckets and templates
const mockTemplates = [
  {
    id: 'tech-startup',
    name: 'Tech Startup',
    description: 'Content buckets for technology startups and SaaS companies',
    buckets: [
      { name: 'Product Updates', description: 'Feature releases, improvements, and announcements', color: '#2196F3' },
      { name: 'Industry Insights', description: 'Tech trends, market analysis, and thought leadership', color: '#4CAF50' },
      { name: 'Behind the Scenes', description: 'Team culture, office life, and company values', color: '#FF9800' },
      { name: 'Customer Success', description: 'User stories, testimonials, and case studies', color: '#9C27B0' },
      { name: 'Educational Content', description: 'How-tos, tutorials, and best practices', color: '#F44336' }
    ]
  },
  {
    id: 'marketing-agency',
    name: 'Marketing Agency',
    description: 'Content strategy for digital marketing agencies',
    buckets: [
      { name: 'Campaign Results', description: 'Client success stories and campaign highlights', color: '#E91E63' },
      { name: 'Marketing Tips', description: 'Industry insights and marketing strategies', color: '#3F51B5' },
      { name: 'Team Spotlights', description: 'Employee features and company culture', color: '#009688' },
      { name: 'Trend Analysis', description: 'Market trends and industry predictions', color: '#795548' }
    ]
  }
];

const mockBuckets = [
  {
    id: 'bucket-1',
    name: 'Product Updates',
    description: 'Feature releases and product announcements',
    color: '#2196F3',
    is_active: true,
    post_count: 15,
    created_at: '2024-01-15T10:00:00Z'
  },
  {
    id: 'bucket-2', 
    name: 'Industry Insights',
    description: 'Tech trends and thought leadership content',
    color: '#4CAF50',
    is_active: true,
    post_count: 23,
    created_at: '2024-01-10T14:30:00Z'
  },
  {
    id: 'bucket-3',
    name: 'Team Updates',
    description: 'Company culture and team highlights',
    color: '#FF9800',
    is_active: true,
    post_count: 8,
    created_at: '2024-01-05T09:15:00Z'
  }
];

export const ContentBucketsProvider = ({ children }) => {
  // Check demo mode directly from localStorage to avoid dependency issues
  const [isDemoMode, setIsDemoMode] = useState(() => {
    return JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
  });
  
  // State management
  const [buckets, setBuckets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contentIdeas, setContentIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
    pageSize: 20
  });

  // Clear error helper
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch user's content buckets
  const fetchBuckets = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      if (isDemoMode) {
        // Use demo data when demo mode is enabled
        setTimeout(() => {
          setBuckets(mockBuckets);
          setPagination(prev => ({
            ...prev,
            count: mockBuckets.length,
            next: null,
            previous: null,
            page: params.page || 1
          }));
          setLoading(false);
        }, 300);
      } else {
        const response = await getContentBuckets(params);
        
        if (response.status) {
          setBuckets(response.data?.results || response.data || []);
          
          // Update pagination if present
          if (response.data?.count !== undefined) {
            setPagination(prev => ({
              ...prev,
              count: response.data.count,
              next: response.data.next,
              previous: response.data.previous,
              page: params.page || 1
            }));
          }
        } else {
          throw new Error(response.message || 'Failed to fetch content buckets');
        }
        setLoading(false);
      }
    } catch (err) {
      if (!isDemoMode) {
        const errorMessage = err.message || 'Failed to fetch content buckets';
        setError(errorMessage);
        logError('ContentBucketsProvider: Failed to fetch buckets', {
          error: errorMessage,
          params,
          timestamp: new Date().toISOString()
        });
      }
      setLoading(false);
    }
  }, [isDemoMode]);

  // Create a new content bucket
  const createBucket = useCallback(async (bucketData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await createContentBucket(bucketData);
      
      if (response.status) {
        const newBucket = response.data;
        setBuckets(prev => [newBucket, ...prev]);
        return newBucket;
      } else {
        throw new Error(response.message || 'Failed to create content bucket');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to create content bucket';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to create bucket', {
        error: errorMessage,
        bucketData,
        timestamp: new Date().toISOString()
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing content bucket
  const updateBucket = useCallback(async (bucketId, bucketData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await updateContentBucket(bucketId, bucketData);
      
      if (response.status) {
        const updatedBucket = response.data;
        setBuckets(prev => 
          prev.map(bucket => 
            bucket.id === bucketId ? updatedBucket : bucket
          )
        );
        return updatedBucket;
      } else {
        throw new Error(response.message || 'Failed to update content bucket');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to update content bucket';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to update bucket', {
        error: errorMessage,
        bucketId,
        bucketData,
        timestamp: new Date().toISOString()
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a content bucket
  const deleteBucket = useCallback(async (bucketId) => {
    try {
      setLoading(true);
      setError(null);
      
      await deleteContentBucket(bucketId);
      
      setBuckets(prev => prev.filter(bucket => bucket.id !== bucketId));
      return true;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete content bucket';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to delete bucket', {
        error: errorMessage,
        bucketId,
        timestamp: new Date().toISOString()
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch industry templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (isDemoMode) {
        // Use demo data when demo mode is enabled
        setTimeout(() => {
          setTemplates(mockTemplates);
          setLoading(false);
        }, 200);
      } else {
        const response = await getIndustryTemplates();
        
        if (response.status) {
          setTemplates(response.data || []);
        } else {
          throw new Error(response.message || 'Failed to fetch industry templates');
        }
        setLoading(false);
      }
    } catch (err) {
      if (!isDemoMode) {
        const errorMessage = err.message || 'Failed to fetch industry templates';
        setError(errorMessage);
        logError('ContentBucketsProvider: Failed to fetch templates', {
          error: errorMessage,
          timestamp: new Date().toISOString()
        });
      }
      setLoading(false);
    }
  }, [isDemoMode]);

  // Create buckets from industry template
  const createFromTemplate = useCallback(async (templateData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await createBucketsFromTemplate(templateData);
      
      if (response.status) {
        const newBuckets = response.data || [];
        setBuckets(prev => [...newBuckets, ...prev]);
        return newBuckets;
      } else {
        throw new Error(response.message || 'Failed to create buckets from template');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to create buckets from template';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to create from template', {
        error: errorMessage,
        templateData,
        timestamp: new Date().toISOString()
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch content ideas for a bucket
  const fetchBucketIdeas = useCallback(async (bucketId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getBucketContentIdeas(bucketId);
      
      if (response.status) {
        const ideas = response.data || [];
        setContentIdeas(ideas);
        return ideas;
      } else {
        throw new Error(response.message || 'Failed to fetch content ideas');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch content ideas';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to fetch bucket ideas', {
        error: errorMessage,
        bucketId,
        timestamp: new Date().toISOString()
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch general content ideas
  const fetchContentIdeas = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getContentIdeas(params);
      
      if (response.results) {
        const ideas = response.results || [];
        setContentIdeas(ideas);
        return ideas;
      } else {
        throw new Error('Failed to fetch content ideas');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch content ideas';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to fetch content ideas', {
        error: errorMessage,
        params,
        timestamp: new Date().toISOString()
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Assign post to bucket
  const assignPost = useCallback(async (postId, bucketId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await assignPostToBucket(postId, bucketId);
      
      if (response.status) {
        // Update bucket post count if needed
        setBuckets(prev => 
          prev.map(bucket => 
            bucket.id === bucketId 
              ? { ...bucket, post_count: (bucket.post_count || 0) + 1 }
              : bucket
          )
        );
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to assign post to bucket');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to assign post to bucket';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to assign post', {
        error: errorMessage,
        postId,
        bucketId,
        timestamp: new Date().toISOString()
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Remove post from bucket
  const removePost = useCallback(async (assignmentId, bucketId) => {
    try {
      setLoading(true);
      setError(null);
      
      await removePostFromBucket(assignmentId);
      
      // Update bucket post count if needed
      setBuckets(prev => 
        prev.map(bucket => 
          bucket.id === bucketId 
            ? { ...bucket, post_count: Math.max((bucket.post_count || 1) - 1, 0) }
            : bucket
        )
      );
      return true;
    } catch (err) {
      const errorMessage = err.message || 'Failed to remove post from bucket';
      setError(errorMessage);
      logError('ContentBucketsProvider: Failed to remove post', {
        error: errorMessage,
        assignmentId,
        bucketId,
        timestamp: new Date().toISOString()
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for demo mode changes
  useEffect(() => {
    const handleDemoModeChange = () => {
      const newDemoMode = JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
      setIsDemoMode(newDemoMode);
    };

    // Listen for custom demo mode events
    window.addEventListener('demoModeChanged', handleDemoModeChange);
    
    return () => {
      window.removeEventListener('demoModeChanged', handleDemoModeChange);
    };
  }, []);

  // Auto-fetch templates on mount and when demo mode changes
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates, isDemoMode]);

  // Provider value
  const value = {
    // State
    buckets,
    templates,
    contentIdeas,
    loading,
    error,
    pagination,
    
    // Actions
    fetchBuckets,
    createBucket,
    updateBucket,
    deleteBucket,
    fetchTemplates,
    createFromTemplate,
    fetchBucketIdeas,
    fetchContentIdeas,
    assignPost,
    removePost,
    clearError,
    
    // Computed values
    activeBuckets: buckets.filter(bucket => bucket.is_active),
    inactiveBuckets: buckets.filter(bucket => !bucket.is_active),
    bucketCount: buckets.length,
    totalPosts: buckets.reduce((sum, bucket) => sum + (bucket.post_count || 0), 0)
  };

  return (
    <ContentBucketsContext.Provider value={value}>
      {children}
    </ContentBucketsContext.Provider>
  );
};

export const useContentBuckets = () => {
  const context = useContext(ContentBucketsContext);
  if (!context) {
    throw new Error('useContentBuckets must be used within ContentBucketsProvider');
  }
  return context;
};

export default ContentBucketsProvider; 